import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CandidateFilterPanel = ({ filters, onFilterChange, onClearFilters, resultCount }) => {
  const [skillInput, setSkillInput] = useState('');
  
  const experienceOptions = [
    { value: 'all', label: 'All Experience Levels' },
    { value: 'fresher', label: 'Fresher' },
    { value: '0-2', label: '0-2 years' },
    { value: '3-5', label: '3-5 years' },
    { value: '5+', label: '5+ years' }
  ];

  const qualificationOptions = [
    { id: 'btech', label: 'B.Tech/B.E.' },
    { id: 'mtech', label: 'M.Tech/M.E.' },
    { id: 'bca', label: 'BCA' },
    { id: 'mca', label: 'MCA' },
    { id: 'bsc', label: 'B.Sc.' },
    { id: 'msc', label: 'M.Sc.' }
  ];

  const locationOptions = [
    { id: 'bangalore', label: 'Bangalore, Karnataka' },
    { id: 'mumbai', label: 'Mumbai, Maharashtra' },
    { id: 'delhi', label: 'Delhi NCR' },
    { id: 'hyderabad', label: 'Hyderabad, Telangana' },
    { id: 'pune', label: 'Pune, Maharashtra' },
    { id: 'ahmedabad', label: 'Ahmedabad, Gujarat' },
    { id: 'chennai', label: 'Chennai, Tamil Nadu' },
    { id: 'kolkata', label: 'Kolkata, West Bengal' }
  ];

  const availabilityOptions = [
    { value: 'all', label: 'All Availability' },
    { value: 'immediate', label: 'Immediate' },
    { value: '1-month', label: 'Within 1 Month' },
    { value: '2-months', label: 'Within 2 Months' },
    { value: '3-months', label: 'Within 3 Months' }
  ];

  const handleQualificationChange = (id, checked) => {
    const current = filters?.qualification || [];
    const updated = checked ? [...current, id] : current?.filter(item => item !== id);
    onFilterChange('qualification', updated);
  };

  const handleLocationChange = (location, checked) => {
    const current = filters?.location || [];
    const updated = checked ? [...current, location] : current?.filter(item => item !== location);
    onFilterChange('location', updated);
  };

  const handleAddSkill = () => {
    if (skillInput?.trim() && !filters?.skills?.includes(skillInput?.trim())) {
      onFilterChange('skills', [...(filters?.skills || []), skillInput?.trim()]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill) => {
    onFilterChange('skills', filters?.skills?.filter(s => s !== skill));
  };

  const handleSalaryChange = (type, value) => {
    const numValue = parseInt(value) || 0;
    onFilterChange('salaryRange', {
      ...filters?.salaryRange,
      [type]: numValue
    });
  };

  const formatSalary = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000)?.toFixed(1)}L`;
    }
    return `₹${(amount / 1000)?.toFixed(0)}K`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 sticky top-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-card-foreground">Filters</h3>
        <Button variant="ghost" size="sm" onClick={onClearFilters}>
          Clear All
        </Button>
      </div>

      <div className="mb-4 p-3 bg-primary/10 rounded-lg">
        <p className="text-sm font-medium text-primary">
          {resultCount} candidate{resultCount !== 1 ? 's' : ''} found
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <Select
            label="Experience Level"
            options={experienceOptions}
            value={filters?.experience}
            onChange={(value) => onFilterChange('experience', value)}
          />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-3 block">
            Qualification
          </label>
          <div className="space-y-2">
            {qualificationOptions?.map(option => (
              <Checkbox
                key={option?.id}
                id={option?.id}
                checked={filters?.qualification?.includes(option?.id)}
                onChange={(e) => handleQualificationChange(option?.id, e?.target?.checked)}
                label={option?.label}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-3 block">
            Skills
          </label>
          <div className="flex gap-2 mb-2">
            <Input
              type="text"
              placeholder="Add skill (e.g., React)"
              value={skillInput}
              onChange={(e) => setSkillInput(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && handleAddSkill()}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddSkill}
              iconName="Plus"
            />
          </div>
          {filters?.skills?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {filters?.skills?.map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                >
                  {skill}
                  <button
                    onClick={() => handleRemoveSkill(skill)}
                    className="hover:bg-primary/20 rounded-full p-0.5"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-3 block">
            Location
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {locationOptions?.map(option => (
              <Checkbox
                key={option?.id}
                id={`location-${option?.id}`}
                checked={filters?.location?.includes(option?.label)}
                onChange={(e) => handleLocationChange(option?.label, e?.target?.checked)}
                label={option?.label}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-3 block">
            Salary Expectations
          </label>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">
                Minimum: {formatSalary(filters?.salaryRange?.min)}
              </label>
              <input
                type="range"
                min="0"
                max="2000000"
                step="50000"
                value={filters?.salaryRange?.min}
                onChange={(e) => handleSalaryChange('min', e?.target?.value)}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">
                Maximum: {formatSalary(filters?.salaryRange?.max)}
              </label>
              <input
                type="range"
                min="0"
                max="2000000"
                step="50000"
                value={filters?.salaryRange?.max}
                onChange={(e) => handleSalaryChange('max', e?.target?.value)}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>₹0</span>
              <span>₹10L</span>
              <span>₹20L</span>
            </div>
          </div>
        </div>

        <div>
          <Select
            label="Availability"
            options={availabilityOptions}
            value={filters?.availability}
            onChange={(value) => onFilterChange('availability', value)}
          />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-3 block">
            Minimum Match Score: {filters?.matchScore}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={filters?.matchScore}
            onChange={(e) => onFilterChange('matchScore', parseInt(e?.target?.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateFilterPanel;